import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Zap, ArrowRight, ArrowDown } from "lucide-react";

export default function RegisterPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    const result = await register(name, email, password);
    setLoading(false);
    if (result.success) {
      navigate("/dashboard");
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-12">
            <div className="w-8 h-8 bg-[var(--pf-accent)] flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" strokeWidth={2} />
            </div>
            <span className="font-heading text-xl font-bold tracking-tight">staqblox</span>
          </div>

          <h1 className="font-heading text-4xl font-bold tracking-tight mb-2" data-testid="register-heading">
            Create account
          </h1>
          <p className="text-[var(--pf-text-secondary)] mb-8">
            Start building better prompts in minutes.
          </p>

          {error && (
            <div className="bg-[var(--pf-error-bg)] border border-[var(--pf-error-border)] text-red-700 dark:text-red-400 px-4 py-3 mb-6 text-sm" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-xs uppercase tracking-[0.2em] font-medium text-[var(--pf-text-secondary)] mb-2 block">
                Full Name
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                required
                data-testid="register-name-input"
                className="rounded-none border-[var(--pf-border)] focus:ring-2 focus:ring-[var(--pf-accent)] h-11"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] font-medium text-[var(--pf-text-secondary)] mb-2 block">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                data-testid="register-email-input"
                className="rounded-none border-[var(--pf-border)] focus:ring-2 focus:ring-[var(--pf-accent)] h-11"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] font-medium text-[var(--pf-text-secondary)] mb-2 block">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 6 characters"
                required
                data-testid="register-password-input"
                className="rounded-none border-[var(--pf-border)] focus:ring-2 focus:ring-[var(--pf-accent)] h-11"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="register-submit-button"
              className="w-full h-11 bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white rounded-none font-medium transition-colors duration-150"
            >
              {loading ? "Creating account..." : "Create account"}
              {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </form>

          <p className="mt-6 text-sm text-[var(--pf-text-secondary)]">
            Already have an account?{" "}
            <Link to="/login" className="text-[var(--pf-accent)] hover:text-[var(--pf-accent-hover)] font-medium" data-testid="register-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 bg-[var(--pf-surface)] items-center justify-center border-l border-[var(--pf-border)]">
        <div className="max-w-md p-12">
          <p className="font-heading text-2xl font-bold tracking-tight mb-6">
            From concept to build-ready prompts
          </p>
          <div className="space-y-4">
            <div className="border border-[var(--pf-border)] bg-[var(--pf-bg)] p-4">
              <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-text-muted)] mb-2">You type</p>
              <p className="text-sm text-[var(--pf-text)] font-mono">"Kanban board with real-time collaboration"</p>
            </div>
            <div className="flex justify-center">
              <ArrowDown className="w-4 h-4 text-[var(--pf-accent)]" />
            </div>
            <div className="border border-[var(--pf-accent)] bg-[var(--pf-bg)] p-4">
              <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-accent)] mb-2">You get</p>
              <p className="text-sm text-[var(--pf-text)]">50+ structured prompt blocks across 5 build phases — scored, refined, and ready to paste into any AI build platform.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
